import { useState, useEffect, type ReactNode } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Box,
} from '@mui/material';

export interface PaginatedTableColumn {
    key: string;
    label: string;
    align?: 'left' | 'center' | 'right';
    sx?: object;
}

export interface PaginatedTableProps<T> {
    columns: PaginatedTableColumn[];
    rows: T[];
    renderRow: (row: T, index: number) => ReactNode;
    rowsPerPage?: number;
    emptyMessage?: string;
    headerSx?: object;
    size?: 'small' | 'medium';
}

const PaginatedTable = <T,>({
    columns,
    rows,
    renderRow,
    rowsPerPage = 10,
    emptyMessage = 'Sin datos disponibles.',
    headerSx,
    size = 'small',
}: PaginatedTableProps<T>) => {
    const [page, setPage] = useState(0);

    // Reset page when rows change
    useEffect(() => {
        setPage(0);
    }, [rows]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const paginatedRows = rows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <Box>
            <TableContainer>
                <Table size={size}>
                    <TableHead>
                        <TableRow sx={headerSx}>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    align={column.align}
                                    sx={{ fontWeight: 600, ...column.sx }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows.map((row, index) => renderRow(row, index))}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > rowsPerPage && (
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[]}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} de ${count}`
                    }
                />
            )}
        </Box>
    );
};

export default PaginatedTable;
